import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BadgeCheck,
  Clock3,
  MessageSquareText,
  Phone,
  ShieldCheck,
  UserRound,
} from 'lucide-react';

const ease = [0.22, 1, 0.36, 1];

const sectionReveal = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.06,
    },
  },
};

const itemReveal = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease },
  },
};

const softRise = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.65, ease },
  },
};

const contactMethods = ['Email', 'Phone', 'WhatsApp'];

const businessTypes = [
  'local service business',
  'gym / fitness',
  'beauty / salon',
  'restaurant / café',
  'real estate',
  'personal brand',
  'other',
];

const projectNeeds = ['New website', 'Website redesign', 'Ongoing support', 'Not sure yet'];

const pricingInterests = ['One-Off Website', 'Membership', 'Not sure yet'];

const nextSteps = [
  'We review your enquiry',
  'We contact you',
  'We discuss the best option',
  'We plan the next step',
];

const CONTACT_EMAIL = 'bbsquareagency@gmail.com';

const reassuranceItems = [
  'Clear communication',
  'Tailored recommendations',
  'No pressure',
  'Built around your business needs',
];

const heroHighlights = ['Structured enquiry', 'Professional guidance', 'Clear next steps'];

function AnimatedGlow({ className, duration, delay, shouldReduceMotion }) {
  return (
    <motion.div
      aria-hidden="true"
      className={className}
      animate={shouldReduceMotion ? undefined : { y: [0, -14, 0], x: [0, 10, 0], scale: [1, 1.05, 1] }}
      transition={
        shouldReduceMotion
          ? undefined
          : { duration, delay, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }
      }
    />
  );
}

function OptionButton({ value, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      className={`rounded-2xl border px-4 py-3 text-left text-sm transition-all duration-300 ${
        selected === value
          ? 'border-cyan-400/40 bg-cyan-500/12 text-white shadow-[0_14px_28px_rgba(34,211,238,0.12)]'
          : 'border-white/10 bg-white/[0.03] text-gray-400 hover:border-white/20 hover:text-gray-200'
      }`}
    >
      {value}
    </button>
  );
}

export default function ContactPage() {
  const shouldReduceMotion = useReducedMotion();
  const hoverLift = shouldReduceMotion
    ? {}
    : { y: -5, scale: 1.01, transition: { duration: 0.25, ease } };

  const [formState, setFormState] = useState({
    fullName: '',
    businessName: '',
    email: '',
    phone: '',
    preferredContactMethod: 'Email',
    businessType: 'local service business',
    whatDoYouNeed: 'New website',
    pricingInterest: 'Not sure yet',
    specialFeatures: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const buildMailtoLink = (values) => {
    const subject = `Website enquiry from ${values.fullName || 'Website lead'}`;
    const bodyLines = [
      `Full Name: ${values.fullName}`,
      `Business Name: ${values.businessName || 'N/A'}`,
      `Email: ${values.email}`,
      `Phone: ${values.phone || 'N/A'}`,
      `Preferred contact method: ${values.preferredContactMethod}`,
      `Business type: ${values.businessType}`,
      `What do you need: ${values.whatDoYouNeed}`,
      `Pricing interest: ${values.pricingInterest}`,
      `Special features: ${values.specialFeatures || 'N/A'}`,
      '',
      'Message / business goals:',
      values.message,
    ];

    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const mailtoLink = buildMailtoLink(formState);
    setIsSubmitted(true);
    setFormState({
      fullName: '',
      businessName: '',
      email: '',
      phone: '',
      preferredContactMethod: 'Email',
      businessType: 'local service business',
      whatDoYouNeed: 'New website',
      pricingInterest: 'Not sure yet',
      specialFeatures: '',
      message: '',
    });

    window.location.href = mailtoLink;
  };

  return (
    <div className="pt-20 overflow-hidden">
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.05),_transparent_36%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.08)_0%,_transparent_34%,_transparent_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,_rgba(255,255,255,0.02)_1px,_transparent_1px),linear-gradient(180deg,_rgba(255,255,255,0.02)_1px,_transparent_1px)] bg-[size:44px_44px] opacity-25" />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={15}
            delay={0}
            className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-cyan-500/14 blur-[120px] sm:h-96 sm:w-96"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={18}
            delay={1.2}
            className="absolute right-[-4rem] top-24 h-56 w-56 rounded-full bg-violet-500/12 blur-[110px] sm:h-72 sm:w-72"
          />
          <AnimatedGlow
            shouldReduceMotion={shouldReduceMotion}
            duration={17}
            delay={0.8}
            className="absolute bottom-0 left-[-4rem] h-52 w-52 rounded-full bg-cyan-400/10 blur-[100px] sm:h-64 sm:w-64"
          />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionReveal}
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div variants={itemReveal} className="mb-6 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-300 backdrop-blur-sm">
              A professional first step for your website project
            </motion.div>

            <motion.h1
              variants={itemReveal}
              className="text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="contact-headline"
            >
              Let&apos;s Talk About Your Website
            </motion.h1>

            <motion.p
              variants={itemReveal}
              className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-gray-300 sm:text-lg lg:text-xl"
            >
              Tell us about your business, what you need, and how you would like to be contacted. We&apos;ll review your enquiry and guide you toward the best next step.
            </motion.p>

            <motion.div variants={sectionReveal} className="mt-10 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {heroHighlights.map((item) => (
                <motion.div
                  key={item}
                  variants={softRise}
                  className="rounded-full border border-white/10 bg-[#101010]/85 px-4 py-2 text-sm font-medium text-gray-200 shadow-[0_12px_32px_rgba(0,0,0,0.24)] backdrop-blur-sm"
                >
                  {item}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(6,182,212,0.03)_0%,_transparent_26%,_transparent_100%)]" />
        </div>

        <div className="section-container relative z-10">
          <div className="grid items-start gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:gap-10">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="order-2 lg:order-1"
            >
              <motion.form
                variants={softRise}
                onSubmit={handleSubmit}
                className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.025))] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.32)] backdrop-blur-sm sm:p-7 lg:p-8"
                data-testid="contact-form"
              >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.14),_transparent_36%)]" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                <div className="relative space-y-6">
                  {isSubmitted && (
                    <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-300">
                      Thanks. Your enquiry has been prepared. We&apos;ll review it and follow up using your preferred contact method.
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="fullName" className="mb-2 block text-sm font-medium text-gray-300">
                        Full Name
                      </label>
                      <input
                        id="fullName"
                        name="fullName"
                        value={formState.fullName}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your full name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="businessName" className="mb-2 block text-sm font-medium text-gray-300">
                        Business Name
                      </label>
                      <input
                        id="businessName"
                        name="businessName"
                        value={formState.businessName}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your business name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-300">
                        Email Address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formState.email}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="you@example.com"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="mb-2 block text-sm font-medium text-gray-300">
                        Phone Number (optional)
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formState.phone}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Preferred contact method
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      {contactMethods.map((method) => (
                        <OptionButton
                          key={method}
                          value={method}
                          selected={formState.preferredContactMethod}
                          onSelect={(value) => handleSelectChange('preferredContactMethod', value)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="businessType" className="mb-2 block text-sm font-medium text-gray-300">
                      Business type
                    </label>
                    <select
                      id="businessType"
                      name="businessType"
                      value={formState.businessType}
                      onChange={handleChange}
                      className="select-field"
                    >
                      {businessTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      What do you need?
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {projectNeeds.map((need) => (
                        <OptionButton
                          key={need}
                          value={need}
                          selected={formState.whatDoYouNeed}
                          onSelect={(value) => handleSelectChange('whatDoYouNeed', value)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Which option are you most interested in?
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      {pricingInterests.map((interest) => (
                        <OptionButton
                          key={interest}
                          value={interest}
                          selected={formState.pricingInterest}
                          onSelect={(value) => handleSelectChange('pricingInterest', value)}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="specialFeatures" className="mb-2 block text-sm font-medium text-gray-300">
                      Special features or requirements
                    </label>
                    <textarea
                      id="specialFeatures"
                      name="specialFeatures"
                      value={formState.specialFeatures}
                      onChange={handleChange}
                      className="textarea-field min-h-[110px]"
                      placeholder="Booking system, extra pages, contact forms, WhatsApp integration, custom functionality, and anything else important"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-300">
                      Message / business goals
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      className="textarea-field min-h-[180px]"
                      placeholder="Tell us about your business, what you want the website to do, and what would make this project successful for you"
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-4 border-t border-white/8 pt-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl text-sm leading-6 text-gray-500">
                      The more context you share, the easier it is for us to recommend the right direction and respond clearly.
                    </p>
                    <button type="submit" className="btn-primary group inline-flex items-center justify-center gap-2 whitespace-nowrap">
                      Send Enquiry
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              </motion.form>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
              variants={sectionReveal}
              className="order-1 space-y-5 lg:order-2"
            >
              <motion.div variants={softRise} className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.055),_rgba(255,255,255,0.02))] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur-sm sm:p-7">
                <div className="mb-5 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_14px_28px_rgba(34,211,238,0.16)]">
                    <UserRound size={22} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-[0.18em] text-cyan-300/90">Before you send</div>
                    <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      What this form is for
                    </h2>
                  </div>
                </div>
                <p className="text-base leading-7 text-gray-300">
                  This form is designed to give us the right context from the start. It helps us understand your business, your priorities, and the kind of website support you are looking for.
                </p>
              </motion.div>

              <motion.div variants={softRise} className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.055),_rgba(255,255,255,0.02))] p-6 shadow-[0_20px_50px_rgba(0,0,0,0.28)] backdrop-blur-sm sm:p-7">
                <div className="mb-5 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_14px_28px_rgba(34,211,238,0.16)]">
                    <MessageSquareText size={22} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-[0.18em] text-cyan-300/90">Helpful details</div>
                    <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      What to include
                    </h2>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    'What your business does',
                    'What kind of website help you need',
                    'Any features you already know you want',
                    'Whether you are leaning toward a one-off build or membership',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/6 bg-black/10 px-4 py-3 text-sm leading-6 text-gray-300">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-cyan-400" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#0d0d0d] py-16 sm:py-20 lg:py-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute right-0 top-12 h-64 w-64 rounded-full bg-cyan-500/8 blur-[120px]" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto max-w-4xl"
          >
            <motion.div variants={itemReveal} className="mb-10 text-center">
              <p className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">What happens next</p>
              <h2 className="text-4xl font-bold leading-tight sm:text-5xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What happens next
              </h2>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-gray-400 sm:text-lg">
                Once you submit the form, we&apos;ll review your enquiry and get back to you using your preferred contact method. From there, we can discuss your goals, recommend the right approach, and move your project forward clearly.
              </p>
            </motion.div>

            <motion.div variants={sectionReveal} className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {nextSteps.map((step, index) => (
                <motion.article
                  key={step}
                  variants={softRise}
                  whileHover={hoverLift}
                  className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.055),_rgba(255,255,255,0.02))] p-5 shadow-[0_18px_44px_rgba(0,0,0,0.24)] backdrop-blur-sm"
                >
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.14),_transparent_34%)] opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="relative">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-500/12 text-sm font-bold text-cyan-300">
                      0{index + 1}
                    </div>
                    <p className="text-lg font-medium text-white">{step}</p>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 sm:py-20 lg:py-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-10 h-52 w-52 -translate-x-1/2 rounded-full bg-violet-500/10 blur-[110px]" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        <div className="section-container relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-12% 0px -10% 0px' }}
            variants={sectionReveal}
            className="mx-auto max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,_rgba(255,255,255,0.06),_rgba(255,255,255,0.025))] px-6 py-8 shadow-[0_24px_70px_rgba(0,0,0,0.34)] backdrop-blur-sm sm:px-8 sm:py-10 lg:px-12"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(6,182,212,0.12),_transparent_34%)]" />
            <div className="relative grid grid-cols-1 gap-5 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
              <motion.div variants={itemReveal}>
                <p className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-cyan-400/90">Reassurance</p>
                <h2 className="text-4xl font-bold leading-tight sm:text-5xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  A professional, low-pressure conversation
                </h2>
              </motion.div>

              <motion.div variants={sectionReveal} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {reassuranceItems.map((item, index) => {
                  const icons = [BadgeCheck, ShieldCheck, Clock3, Phone];
                  const Icon = icons[index];

                  return (
                    <motion.div
                      key={item}
                      variants={softRise}
                      whileHover={hoverLift}
                      className="flex items-start gap-3 rounded-2xl border border-white/8 bg-black/10 px-4 py-4 text-sm text-gray-300"
                    >
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 shadow-[0_10px_24px_rgba(34,211,238,0.16)]">
                        <Icon size={16} className="text-white" />
                      </div>
                      <span className="leading-6">{item}</span>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}